/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ColDef } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { weightBold } from 'src/components/css'
import DataGrid from 'src/components/ts-components/DataGrid'

interface RosterSectionProps<TRow extends { id: string }> {
  title: string
  rows: TRow[]
  columnDefs: ColDef<TRow>[]
  // Shown in the grid when it has no rows.
  emptyMessage: string
  'data-testid'?: string
}

// Fits the compact 32px avatar and a two-line name (name + customer id).
const ROW_HEIGHT = 48
const HEADER_HEIGHT = 40
const CELL_CLASS = 'roster-cell'

/** One roster table (Players, Spares, Team Officials): its title + grid. */
const RosterSection = <TRow extends { id: string }>({
  title,
  rows,
  columnDefs,
  emptyMessage,
  'data-testid': testId,
}: RosterSectionProps<TRow>) => (
  <section css={sectionStyle}>
    <p className='player-list-affiliates-title' css={titleStyle}>
      {title}
    </p>
    <DataGrid<TRow>
      css={gridStyle}
      rowData={rows}
      columnDefs={columnDefs}
      getRowId={({ data }) => data.id}
      // Sorting is opt-in per column (the roster only sorts by name, # and
      // position).
      defaultColDef={{ sortable: false, cellClass: CELL_CLASS }}
      domLayout='autoHeight'
      rowHeight={ROW_HEIGHT}
      headerHeight={HEADER_HEIGHT}
      suppressCellFocus
      noRowsOverlayComponentParams={{ message: emptyMessage }}
      data-testid={testId}
    />
  </section>
)

export default RosterSection

// ---- styled ----
// `min-width: 0` lets the grid scroll its own columns on a narrow screen
// instead of pushing the page sideways.
const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`
// Centre every cell vertically with a normal line-height — AG Grid's default
// (line-height = row height) clips the two-line name cell.
const gridStyle = css`
  .${CELL_CLASS} {
    display: flex;
    align-items: center;
    line-height: normal;
  }
`
const titleStyle = css`
  ${weightBold}
  color: ${colors.DEFAULT_FLAIR};
  text-transform: uppercase;
  margin: 0;
`
